'use client';

import { useState, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  X,
  Plus,
  Search,
  ArrowRight,
  CheckCircle,
  XCircle,
  Package,
  Building2,
  Tag,
  Layers,
} from 'lucide-react';
import type { ProductFamily } from '@/lib/data/software';

interface CompareClientProps {
  allFamilies: ProductFamily[];
  initialProducts: ProductFamily[];
}

export default function CompareClient({ allFamilies, initialProducts }: CompareClientProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [selectedProducts, setSelectedProducts] = useState<ProductFamily[]>(initialProducts);
  const [showSelector, setShowSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Update URL when products change
  useEffect(() => {
    const slugs = selectedProducts.map((p) => p.slug).join(',');
    const params = new URLSearchParams(searchParams.toString());
    if (slugs) {
      params.set('products', slugs);
    } else {
      params.delete('products');
    }
    const newUrl = `/software/compare${params.toString() ? `?${params.toString()}` : ''}`;
    router.replace(newUrl, { scroll: false });
  }, [selectedProducts, router, searchParams]);

  // Filter products for selector
  const availableProducts = useMemo(() => {
    const selectedSlugs = new Set(selectedProducts.map((p) => p.slug));
    let filtered = allFamilies.filter((f) => !selectedSlugs.has(f.slug));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (f) =>
          f.name.toLowerCase().includes(query) ||
          f.vendorName.toLowerCase().includes(query) ||
          f.tagline?.toLowerCase().includes(query)
      );
    }

    return filtered.slice(0, 20);
  }, [allFamilies, selectedProducts, searchQuery]);

  const addProduct = (product: ProductFamily) => {
    if (selectedProducts.length < 4) {
      setSelectedProducts([...selectedProducts, product]);
      setShowSelector(false);
      setSearchQuery('');
    }
  };

  const removeProduct = (slug: string) => {
    setSelectedProducts(selectedProducts.filter((p) => p.slug !== slug));
  };

  // Get all unique tags across selected products
  const allTags = useMemo(() => {
    const tagMap = new Map<string, { name: string; products: Set<string> }>();
    selectedProducts.forEach((product) => {
      product.tags?.forEach((tag) => {
        if (!tagMap.has(tag.slug)) {
          tagMap.set(tag.slug, { name: tag.name, products: new Set() });
        }
        tagMap.get(tag.slug)!.products.add(product.slug);
      });
    });
    return Array.from(tagMap.entries())
      .map(([slug, data]) => ({
        slug,
        name: data.name,
        products: data.products,
      }))
      .sort((a, b) => b.products.size - a.products.size);
  }, [selectedProducts]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Product Selector Slots */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[0, 1, 2, 3].map((index) => {
          const product = selectedProducts[index];
          return (
            <div
              key={index}
              className={`relative rounded-xl border-2 ${
                product
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : 'border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
              } p-4 min-h-[160px] flex flex-col`}
            >
              {product ? (
                <>
                  <button
                    onClick={() => removeProduct(product.slug)}
                    className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label="Remove product"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    {product.iconUrl ? (
                      <img
                        src={product.iconUrl}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-contain bg-gray-50 dark:bg-gray-800"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {product.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{product.vendorName}</p>
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm truncate">
                        {product.name}
                      </h3>
                    </div>
                  </div>
                  <Link
                    href={`/software/${product.slug}`}
                    className="mt-auto text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  >
                    View details &rarr;
                  </Link>
                </>
              ) : (
                <button
                  onClick={() => setShowSelector(true)}
                  disabled={selectedProducts.length >= 4}
                  className="flex-1 flex flex-col items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-400 transition-colors disabled:opacity-50"
                >
                  <Plus className="w-8 h-8 mb-2" />
                  <span className="text-sm">Add Product</span>
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Product Selector Modal */}
      {showSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select a Product</h2>
                <button
                  onClick={() => {
                    setShowSelector(false);
                    setSearchQuery('');
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  aria-label="Search products" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-[50vh]">
              {availableProducts.length > 0 ? (
                <div className="divide-y">
                  {availableProducts.map((product) => (
                    <button
                      key={product.slug}
                      onClick={() => addProduct(product)}
                      className="w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex items-center gap-3"
                    >
                      {product.iconUrl ? (
                        <img
                          src={product.iconUrl}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-contain bg-gray-100 dark:bg-gray-800"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {product.name.charAt(0)}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{product.vendorName}</p>
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{product.name}</h3>
                        {product.tagline && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{product.tagline}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No products found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      {selectedProducts.length >= 2 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50 dark:bg-gray-800">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 w-48">
                    Feature
                  </th>
                  {selectedProducts.map((product) => (
                    <th key={product.slug} className="px-6 py-4 text-center min-w-[180px]">
                      <div className="flex flex-col items-center">
                        {product.iconUrl ? (
                          <img
                            src={product.iconUrl}
                            alt={product.name}
                            className="w-12 h-12 rounded-lg object-contain bg-gray-50 dark:bg-gray-800 mb-2"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-2">
                            <span className="text-lg font-bold text-white">
                              {product.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">{product.name}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {/* Vendor */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Vendor
                  </td>
                  {selectedProducts.map((product) => (
                    <td key={product.slug} className="px-6 py-4 text-center text-sm">
                      <Link
                        href={`/software/vendor/${product.vendorName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      >
                        {product.vendorName}
                      </Link>
                    </td>
                  ))}
                </tr>

                {/* Category */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Layers className="w-4 h-4" />
                    Category
                  </td>
                  {selectedProducts.map((product) => (
                    <td key={product.slug} className="px-6 py-4 text-center text-sm">
                      {product.category ? (
                        <Link
                          href={`/software/category/${product.category.slug}`}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {product.category.name}
                        </Link>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Variants/Plans */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Available Plans
                  </td>
                  {selectedProducts.map((product) => (
                    <td key={product.slug} className="px-6 py-4 text-center text-sm">
                      <span className="font-medium">{product.variantCount || 1}</span>
                      {product.variantCount && product.variantCount > 1 && (
                        <Link
                          href={`/software/${product.slug}/compare`}
                          className="block text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-1"
                        >
                          Compare plans
                        </Link>
                      )}
                    </td>
                  ))}
                </tr>

                {/* Tags Comparison */}
                {allTags.slice(0, 15).map((tag) => (
                  <tr key={tag.slug} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      {tag.name}
                    </td>
                    {selectedProducts.map((product) => (
                      <td key={product.slug} className="px-6 py-4 text-center">
                        {tag.products.has(product.slug) ? (
                          <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="w-5 h-5 text-gray-300 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Tagline */}
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800 bg-gray-50 dark:bg-gray-800">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Description</td>
                  {selectedProducts.map((product) => (
                    <td key={product.slug} className="px-6 py-4 text-center text-sm text-gray-600 dark:text-gray-400">
                      {product.tagline || '—'}
                    </td>
                  ))}
                </tr>

                {/* Action Row */}
                <tr className="bg-blue-50 dark:bg-blue-950/40">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">Get Started</td>
                  {selectedProducts.map((product) => (
                    <td key={product.slug} className="px-6 py-4 text-center">
                      <Link
                        href={`/software/${product.slug}`}
                        className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        View Details
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">Select Products to Compare</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Add at least 2 products above to start comparing features and capabilities.
          </p>
          <button
            onClick={() => setShowSelector(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Add Product
          </button>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-8 text-center">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Need help choosing? Our experts can provide personalized recommendations.
        </p>
        <Link
          href="/contact?source=software-compare&cta=comparison-tool"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
        >
          Talk to an Expert
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
